import { useEffect, useMemo, useState } from "react";
import { geoAlbersUsa, geoNaturalEarth1, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import { Panel, Load, NoData, VTable } from "@/components/vi/ui";
import { useVi, useViState } from "@/lib/vi";

const SRC = { world: ["https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json", "countries"], us: ["https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json", "states"] };
const cache = {};
const ALIAS = { "united states": "united states of america", usa: "united states of america", us: "united states of america", uk: "united kingdom", "czech republic": "czechia", "south korea": "south korea", russia: "russia" };
const regionName = (() => { try { return new Intl.DisplayNames(["en"], { type: "region" }); } catch { return null; } })();
const norm = (k) => { let s = String(k || "").trim(); if (/^[A-Z]{2}$/.test(s) && regionName) s = regionName.of(s) || s; s = s.toLowerCase(); return ALIAS[s] || s; };

const useTopo = (which) => {
  const [g, setG] = useState(cache[which] || null);
  useEffect(() => { if (cache[which]) return; fetch(SRC[which][0]).then((r) => r.json()).then((t) => { cache[which] = feature(t, t.objects[SRC[which][1]]).features; setG(cache[which]); }).catch(() => setG([])); }, [which]);
  return g;
};

const Choropleth = ({ which, rows, dim, testid }) => {
  const { setFilter } = useViState();
  const feats = useTopo(which);
  const [hover, setHover] = useState(null);
  const W = 960, H = which === "us" ? 560 : 480;
  const path = useMemo(() => geoPath(which === "us" ? geoAlbersUsa().scale(1150).translate([W / 2, H / 2]) : geoNaturalEarth1().scale(175).translate([W / 2, H / 2])), [which, H]);
  const map = useMemo(() => Object.fromEntries((rows || []).map((r) => [norm(r.key), r])), [rows]);
  const max = Math.max(1, ...(rows || []).map((r) => r.visitors || 0));
  if (!feats) return <p className="text-xs text-zinc-500">Loading map…</p>;
  if (!feats.length) return <NoData why="map shapes couldn't load; the table below has the same data." />;
  return (
    <div className="relative" data-testid={testid}>
      <svg viewBox={`0 0 ${W} ${H}`} className="h-auto w-full">{feats.map((f, i) => { const r = map[norm(f.properties.name)]; const t = r ? Math.max(0.15, (r.visitors || 0) / max) : 0;
        return <path key={i} d={path(f) || ""} fill={r ? `rgba(212,175,55,${t})` : "#16161a"} stroke="#27272A" strokeWidth={0.5} className={r ? "cursor-pointer hover:stroke-[#F3C649]" : ""} onMouseEnter={() => setHover({ n: f.properties.name, r })} onMouseLeave={() => setHover(null)} onClick={() => r && setFilter(dim, r.key)} data-testid={r ? `${testid}-shape-${norm(r.key).replace(/\s+/g, "-")}` : undefined} />; })}</svg>
      {hover && <div className="pointer-events-none absolute left-3 top-3 rounded-lg border border-[#27272A] bg-black/80 px-3 py-1.5 text-xs text-zinc-200">{hover.n}: {hover.r ? `${hover.r.visitors} visitors` : "no visitors"}</div>}
    </div>
  );
};

const Geo = ({ dim, which, t }) => {
  const q = useVi(`/admin/analytics/breakdown?dim=${dim}`);
  return <Panel t={t} right={<span className="text-[10px] text-zinc-500">Approximate, IP-based</span>} testid={`vi-geo-${dim}`}><Load q={q}>{(d) => <div className="space-y-3">
    {which && <Choropleth which={which} rows={d.rows} dim={dim} testid={`vi-map-${which}`} />}
    <VTable rows={d.rows} cols={["key", "visitors", "sessions", "registrations", "conversion_rate", "revenue"]} dim={dim} testid={`vi-geo-${dim}-table`} />
  </div>}</Load></Panel>;
};

export const Geography = () => <div className="space-y-4" data-testid="vi-geography"><Geo dim="country" which="world" t="World" /><Geo dim="region" which="us" t="US states & regions" /><Geo dim="city" t="Cities" /></div>;
